import { useCallback, useMemo, useState } from 'react'
import type { Dayjs } from 'dayjs'
import { UserPlus } from 'lucide-react'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import type { DataTableSelectionAction } from '@/components/ui/data-table-customized'
import { useModal } from '@/contexts/modal'
import { useTicketList } from '@/modules/Ticket/hooks'
import type { Ticket } from '@/modules/Ticket/types/response'
import type { AssignTicketModalProps } from '@/modals/TicketModals/AssignTicket'
import ExportTicketButton from '../ExportButton'

interface ListProps {
    filterTime: [Dayjs | null, Dayjs | null]
}

const List: React.FC<ListProps> = ({ filterTime }) => {
    const { setModal } = useModal()
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    const {
        tickets,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useTicketList({ filterTime })

    // Handle bulk assign
    const handleBulkAssign = useCallback(
        (selectedRows: Ticket[]) => {
            setModal<AssignTicketModalProps>(
                'TicketModals/AssignTicket/index',
                {
                    tickets: selectedRows,
                    onSuccess: () => setSelectedKeys([]),
                }
            )
        },
        [setModal]
    )

    // Selection actions
    const selectionActions = useMemo<DataTableSelectionAction<Ticket>[]>(
        () => [
            {
                key: 'bulk-assign',
                label: t('bulk-assign-tickets', { count: selectedKeys.length }),
                icon: <UserPlus className="w-4 h-4" />,
                variant: 'info',
                onClick: handleBulkAssign,
            },
        ],
        [t, selectedKeys.length, handleBulkAssign]
    )

    return (
        <Paper data-tour="ticket-list">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />

            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={tickets}
                    rowKey={(row) => String(row?.ticket_id)}
                    size="small"
                    resizable
                    loading={!tickets.length && !pagination}
                    isFetching={isFetching}
                    emptyMessage={t('no-tickets')}
                    selection={{
                        selectedKeys,
                        onSelectionChange: setSelectedKeys,
                        getRowKey: (row) => String(row?.ticket_id),
                        canSelect: (row) =>
                            row?.ticket_status === 'opening' &&
                            (!row?.ticket_technical_assignees ||
                                row?.ticket_technical_assignees?.length === 0),
                        actions: selectionActions,
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page || 1,
                        pageSize: params.page_size || 10,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search-ticket'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'ticket-list',
                            actionComponents: [
                                <ExportTicketButton key="export" />,
                            ],
                        },
                    }}
                />
            )}
        </Paper>
    )
}

export default List
